import React from 'react';
import { motion } from 'framer-motion';
import { 
  ChevronRight, 
  Lightbulb, 
  Target, 
  TrendingUp, 
  CheckCircle, 
  ArrowRight, 
  Zap, 
  BarChart3, 
  Award,
  Star,
  Sparkles,
  Bot,
  Brain,
  Rocket
} from 'lucide-react';

const AiRecommendationsComparison: React.FC = () => {
  const mockRecommendations = [
    {
      title: "Optimisez le score mobile en réduisant le temps de chargement des pages.",
      solution: "Intégrez un plugin de mise en cache comme WP Rocket pour améliorer la vitesse et l'expérience utilisateur sur mobile.",
      priority: "high"
    },
    {
      title: "Mettez à jour vos plugins WordPress régulièrement et supprimez ceux inutilisés.",
      solution: "Cela garantira non seulement la sécurité du site, mais aussi une meilleure performance globale.",
      priority: "medium"
    },
    {
      title: "Intégrez des mots-clés pertinents dans vos contenus pour améliorer votre référencement.",
      solution: "Réalisez une recherche de mots-clés avec des outils comme SEMrush ou Google Keyword Planner pour cibler efficacement votre audience.",
      priority: "high"
    },
    {
      title: "Créez un blog sur votre site pour générer du contenu frais et engageant.",
      solution: "Publier régulièrement des articles optimisés pour le SEO renforcera votre visibilité et attirera du trafic qualifié.",
      priority: "medium"
    }
  ];

  // Option 1: Style Tech Dashboard (cohérent avec TestPage)
  const Option1TechDashboard = () => (
    <div className="bg-white p-8 rounded-2xl shadow-lg border border-gray-200">
      <div className="text-center mb-8">
        <div className="inline-flex items-center px-4 py-2 rounded-full bg-orange-100 border border-orange-200 text-orange-700 text-sm font-medium mb-6">
          <div className="w-2 h-2 bg-orange-500 rounded-full animate-pulse mr-2"></div>
          <Bot className="w-4 h-4 mr-2" />
          Recommandations IA
        </div>
        <h3 className="text-3xl font-bold text-gray-900 mb-4">
          Plan d'action <span className="text-orange-600">personnalisé</span>
        </h3>
        <p className="text-lg text-gray-600">
          Nos recommandations basées sur l'IA pour améliorer votre performance digitale
        </p>
      </div>

      <div className="grid gap-6">
        {mockRecommendations.map((rec, index) => (
          <div key={index} className="bg-gray-50 rounded-xl p-6 border border-gray-200">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center text-orange-600 font-bold text-sm">
                  {index + 1}
                </div>
              </div>
              <div className="flex-1">
                <h4 className="font-semibold text-gray-900 mb-2">{rec.title}</h4>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-sm text-blue-800">
                    <span className="font-medium text-blue-600">Solution:</span> {rec.solution}
                  </p>
                </div>
                <div className="mt-3 flex items-center gap-2">
                  <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                    rec.priority === 'high' 
                      ? 'bg-red-100 text-red-700' 
                      : 'bg-yellow-100 text-yellow-700'
                  }`}>
                    Priorité {rec.priority === 'high' ? 'élevée' : 'moyenne'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  // Option 2: Style Cards Modernes avec animations
  const Option2ModernCards = () => (
    <div className="bg-gradient-to-br from-gray-900 to-gray-800 p-8 rounded-3xl shadow-2xl">
      <div className="text-center mb-8">
        <motion.div 
          className="inline-flex items-center px-4 py-2 rounded-full bg-orange-500/20 border border-orange-500/30 text-orange-400 text-sm font-medium mb-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <Sparkles className="w-4 h-4 mr-2" />
          Intelligence Artificielle
        </motion.div>
        <motion.h3 
          className="text-3xl font-bold text-white mb-4"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          Stratégie <span className="text-orange-400">optimisée</span>
        </motion.h3>
        <motion.p 
          className="text-xl text-gray-300"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          Recommandations personnalisées pour maximiser votre impact digital
        </motion.p>
      </div>

      <div className="grid gap-6">
        {mockRecommendations.map((rec, index) => (
          <motion.div 
            key={index}
            className="bg-gray-700/50 backdrop-blur-sm rounded-2xl p-6 border border-gray-600/50 hover:border-orange-500/50 transition-all duration-300"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.5 + index * 0.1 }}
            whileHover={{ scale: 1.02, y: -2 }}
          >
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center text-white font-bold">
                  {index + 1}
                </div>
              </div>
              <div className="flex-1">
                <h4 className="font-semibold text-white mb-3 flex items-center gap-2">
                  <Target className="w-4 h-4 text-orange-400" />
                  {rec.title}
                </h4>
                <div className="bg-orange-900/30 border border-orange-700/50 rounded-xl p-4 mb-4">
                  <p className="text-sm text-orange-200">
                    <span className="font-medium text-orange-400">💡 Solution:</span> {rec.solution}
                  </p>
                </div>
                <div className="flex items-center justify-between">
                  <span className={`text-xs px-3 py-1 rounded-full font-medium ${
                    rec.priority === 'high' 
                      ? 'bg-red-500/20 text-red-400 border border-red-500/30' 
                      : 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
                  }`}>
                    {rec.priority === 'high' ? '🔥 Priorité élevée' : '⚡ Priorité moyenne'}
                  </span>
                  <motion.button 
                    className="text-orange-400 hover:text-orange-300 transition-colors"
                    whileHover={{ x: 5 }}
                  >
                    <ArrowRight className="w-4 h-4" />
                  </motion.button>
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );

  // Option 3: Style Minimaliste (simplifié)
  const Option3InteractiveDashboard = () => (
    <div className="bg-white rounded-3xl shadow-xl border border-gray-200 overflow-hidden">
      {/* Header minimaliste */}
      <div className="p-8">
        <div className="text-center mb-8">
          <div className="inline-flex items-center px-4 py-2 rounded-full bg-orange-100 border border-orange-200 text-orange-700 text-sm font-medium mb-6">
            <div className="w-2 h-2 bg-orange-500 rounded-full animate-pulse mr-2"></div>
            <Brain className="w-4 h-4 mr-2" />
            Recommandations IA
          </div>
          <h3 className="text-3xl font-bold text-gray-900 mb-4">
            Plan d'action <span className="text-orange-600">personnalisé</span>
          </h3>
          <p className="text-lg text-gray-600">
            Nos recommandations basées sur l'IA pour améliorer votre performance digitale
          </p>
        </div>

        {/* Liste des recommandations - Style minimaliste */}
        <div className="space-y-6 mb-8">
          {mockRecommendations.map((rec, index) => (
            <div key={index} className="border-l-4 border-orange-500 pl-6 py-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-6 h-6 rounded-full bg-orange-500 text-white text-xs font-bold flex items-center justify-center">
                    {index + 1}
                  </div>
                  <h4 className="font-semibold text-gray-900">
                    {rec.title}
                  </h4>
                </div>
                <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                  rec.priority === 'high' 
                    ? 'bg-orange-100 text-orange-700' 
                    : 'bg-gray-100 text-gray-700'
                }`}>
                  {rec.priority === 'high' ? 'Élevée' : 'Moyenne'}
                </span>
              </div>
              <div className="bg-gray-50 rounded-lg p-4 ml-9">
                <p className="text-sm text-gray-700">
                  <span className="font-medium text-orange-600">Solution:</span> {rec.solution}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Footer noir simplifié */}
      <div className="bg-gray-800 rounded-b-3xl p-6">
        <div className="flex items-start gap-4">
          <div className="p-2 bg-orange-500/20 rounded-lg flex-shrink-0">
            <TrendingUp className="w-5 h-5 text-orange-400" />
          </div>
          <div>
            <h4 className="font-semibold text-white mb-3">
              Recommandations générales
            </h4>
            <p className="text-sm text-gray-300 leading-relaxed">
              Ces 4 optimisations peuvent <span className="font-semibold text-orange-400">améliorer significativement votre performance</span>. 
              Commencez par les actions prioritaires pour un impact maximal.
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="container mx-auto px-4">
        <div className="max-w-7xl mx-auto">
          
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-orange-100 border border-orange-200 text-orange-700 text-sm font-medium mb-6">
              <Award className="w-4 h-4 mr-2" />
              Comparaison des designs
            </div>
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Section Recommandations IA
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Choisissez le style qui correspond le mieux à l'esthétique de votre page TestPage.tsx
            </p>
          </div>

          {/* Options Grid */}
          <div className="space-y-16">
            
            {/* Option 1 */}
            <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-200">
              <div className="mb-8">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-2xl font-bold text-gray-900">Option 1: Tech Dashboard</h2>
                  <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium">
                    Cohérent avec TestPage
                  </span>
                </div>
                <p className="text-gray-600">
                  Style cohérent avec les autres sections de TestPage.tsx. Design propre avec badges orange et cartes grises.
                </p>
                <div className="flex flex-wrap gap-2 mt-3">
                  <span className="bg-orange-100 text-orange-700 px-2 py-1 rounded text-xs">Orange/Gris</span>
                  <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded text-xs">Cards</span>
                  <span className="bg-purple-100 text-purple-700 px-2 py-1 rounded text-xs">Numérotation</span>
                </div>
              </div>
              <Option1TechDashboard />
            </div>

            {/* Option 2 */}
            <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-200">
              <div className="mb-8">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-2xl font-bold text-gray-900">Option 2: Modern Cards</h2>
                  <span className="bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-sm font-medium">
                    Animations avancées
                  </span>
                </div>
                <p className="text-gray-600">
                  Design moderne avec fond sombre et animations Framer Motion. Cards flottantes avec effets hover sophistiqués.
                </p>
                <div className="flex flex-wrap gap-2 mt-3">
                  <span className="bg-gray-100 text-gray-700 px-2 py-1 rounded text-xs">Sombre</span>
                  <span className="bg-orange-100 text-orange-700 px-2 py-1 rounded text-xs">Animations</span>
                  <span className="bg-green-100 text-green-700 px-2 py-1 rounded text-xs">Hover Effects</span>
                </div>
              </div>
              <Option2ModernCards />
            </div>

            {/* Option 3 */}
            <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-200">
              <div className="mb-8">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-2xl font-bold text-gray-900">Option 3: Interactive Dashboard</h2>
                  <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
                    Dashboard style
                  </span>
                </div>
                <p className="text-gray-600">
                  Interface de type dashboard avec header coloré, statistiques et actions interactives. Style professionnel et fonctionnel.
                </p>
                <div className="flex flex-wrap gap-2 mt-3">
                  <span className="bg-orange-100 text-orange-700 px-2 py-1 rounded text-xs">Header coloré</span>
                  <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded text-xs">Stats</span>
                  <span className="bg-green-100 text-green-700 px-2 py-1 rounded text-xs">Interactif</span>
                </div>
              </div>
              <Option3InteractiveDashboard />
            </div>

          </div>

          {/* Navigation */}
          <div className="mt-16 text-center">
            <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-200">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Choisissez votre option préférée</h3>
              <p className="text-gray-600 mb-6">
                Quelle option correspond le mieux au style de votre page TestPage.tsx ?
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <button className="bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 rounded-xl font-medium transition-colors">
                  Option 1 - Tech Dashboard
                </button>
                <button className="bg-purple-500 hover:bg-purple-600 text-white px-6 py-3 rounded-xl font-medium transition-colors">
                  Option 2 - Modern Cards
                </button>
                <button className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-3 rounded-xl font-medium transition-colors">
                  Option 3 - Interactive Dashboard
                </button>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default AiRecommendationsComparison;
